import * as z from 'zod/v4';

import { drawNumberSchema } from '../draw/entry.js';
import { couponProductEnum } from '../product/entry.js';
import { eventNumberSchema } from '../sport/entry.js';
import { RSYS_VARIANTS, systemTypeEnum, USYS_VARIANTS } from '../system/entry.js';
import { quickPickEnum } from './boardProps.js';
import { couponTypeSchema } from './CouponId.js';
import { sportkryssCouponSchema } from './SportkryssCoupon.js';

/**
 * Coupon outcome schema
 */
export const outcomeSchema = z.number().int().brand<'Outcome'>();

/**
 * Coupon outcome type
 */
export type Outcome = z.infer<typeof outcomeSchema>;

/**
 * Coupon outcome group schema
 */
export const outcomeGroupSchema = z.array(outcomeSchema);

/**
 * Coupon outcome group type
 */
export type OutcomeGroup = z.infer<typeof outcomeGroupSchema>;

/**
 * Coupon bet event schema
 */
export const betEventSchema = z.array(outcomeGroupSchema);

/**
 * TS Coupon bet event type
 */
export type BetEvent = z.infer<typeof betEventSchema>;

/**
 * Coupon bet schema
 */
export const betSchema = z.array(betEventSchema);

/**
 * TS Coupon bet type
 */
export type Bet = z.infer<typeof betSchema>;

/**
 * Coupon outcome distribution entity
 */
const reductionDistributionOutcome = z.number().brand<'ReductionDistributionOutcome'>();

/**
 * Coupon outcome distribution outcome
 */
export type ReductionDistributionOutcome = z.infer<typeof reductionDistributionOutcome>;

/**
 * Coupon outcome distribution schema
 */
const outcomeDistributionSchema = z.array(z.array(z.array(reductionDistributionOutcome)));

/**
 * TS Coupon outcome distribution type
 */
export type OutcomeDistribution = z.infer<typeof outcomeDistributionSchema>;

/*
  This const is necessary to avoid json stringify to set undefined to null.
  But since we have lint rule to not use null we decided to disable this rule for this case
*/
// eslint-disable-next-line unicorn/no-null
export const NO_VALUE_SET = null;

/**
 * Coupon weighted U outcomes schema
 */
export const uOutcomesSchema = z.array(z.union([z.number(), z.literal(NO_VALUE_SET)]));

/**
 * TS Coupon weighted U outcomes type
 */
export type UOutcomes = z.infer<typeof uOutcomesSchema>;

/**
 * Coupon math outcomes schema
 */
const mOutcomesSchema = z.array(z.boolean());

/**
 * TS Coupon math outcomes type
 */
export type MOutcomes = z.infer<typeof mOutcomesSchema>;

/**
 * Coupon board type schema
 */
export const boardTypeEnum = z.enum(['BOARD_SINGLE', 'BOARD_MULTIPLE']);

/**
 * TS Coupon board type
 */
export type BoardType = z.infer<typeof boardTypeEnum>;

/**
 * Coupon last selected system schema
 */
export const lastSelectedSystem = systemTypeEnum.exclude(['SROW', 'MSYS']);

/**
 * TS Coupon last selected system type
 */
export type LastSelectedSystem = z.infer<typeof lastSelectedSystem>;

/**
 * Coupon reduction values schema
 */
export const reductionValues = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
]);

/**
 * Coupon reduction schema
 */
export type Reduction = z.infer<typeof reductionValues>;

/**
 * Coupon reduction schema
 */
export const reductionSchema = z.array(reductionValues).optional();

/**
 * Coupon board schema
 */
export const boardSchema = z.object({
  type: boardTypeEnum,
  quickPickSize: z.number().int().optional(),
  quickPickVariant: quickPickEnum.optional(),
  outcomes: betSchema,
  userOutcomes: betSchema,
  outcomeDistribution: outcomeDistributionSchema,
  uOutcomes: uOutcomesSchema,
  mOutcomes: mOutcomesSchema,
  reduction: reductionSchema,
  // systemSize: z.number().optional(),
});

/**
 * TS Coupon board type
 */
export type Board = z.infer<typeof boardSchema>;

/**
 * Coupon week (number of rounds) schema
 */
export const numberOfDrawsSchema = z.union([z.literal(1), z.literal(5), z.literal(10)]);

/**
 * TS Coupon week (number of rounds) type
 */
export type NumberOfDrawsValues = z.infer<typeof numberOfDrawsSchema>;

/**
 * Coupon schema used in tipsen products, main entity
 */
export const couponSchema = z.object({
  ...sportkryssCouponSchema.pick({ sportkryss: true, sportkryssNumber: true }).shape,
  product: couponProductEnum,
  drawNumber: drawNumberSchema,
  boards: z.array(boardSchema),
  betAmount: z.number().int(),
  week: numberOfDrawsSchema.default(1),
  uSystem: z.enum(USYS_VARIANTS).optional(),
  rSystem: z.enum(RSYS_VARIANTS).optional(),
  selectedSystem: systemTypeEnum.default('MSYS'),
  lastSelectedSystemType: lastSelectedSystem.optional(),
  reduceModeCount: z.number().int().min(20).max(2200).optional(),
  xpertAnalysisAuthorId: z.string().optional(),
  couponType: couponTypeSchema,
});

/**
 * TS Coupon type
 */
export type Coupon = z.infer<typeof couponSchema>;

const couponValidatorSchema = couponSchema
  .pick({
    betAmount: true,
    week: true,
    uSystem: true,
    rSystem: true,
    selectedSystem: true,
    lastSelectedSystemType: true,
    reduceModeCount: true,
    xpertAnalysisAuthorId: true,
  })
  .partial();

/**
 * Coupon validator to partially validate coupon payload
 */
export const couponValidator = {
  parse: (data: unknown) => couponValidatorSchema.parse(data),
} as const;

const outcomePayloadSchema = z.object({
  group: z.number().int().min(0),
  event: eventNumberSchema,
  outcomes: z.array(outcomeSchema),
});

const boardValidatorSchema = z
  .object({
    ...boardSchema.pick({
      quickPickSize: true,
      quickPickVariant: true,
      uOutcomes: true,
      mOutcomes: true,
      reduction: true,
    }).shape,
    outcomes: z.object({
      values: z.array(outcomePayloadSchema),
      intention: z.enum(['keep', 'reset']).optional(),
    }),
  })
  .partial();

/**
 * Coupon board validator to partially validate coupon board payload
 */
export const couponBoardValidator = {
  parse: (data: unknown) => boardValidatorSchema.parse(data),
} as const;

/**
 * Checks wether a value is of type outcome or not.
 * This is a TypeScript assertion function that will cast the value to a branded Outcome type if it is Outcome like.
 *
 * @param value The value to be checked
 * @returns true if the value is of type Outcome
 */
export function isOutcome(value: unknown): value is Outcome {
  if (typeof value !== 'number') {
    return false;
  }

  // Important check, negative value used in bitmap parsing from wager API
  if (value < 0) {
    return false;
  }

  return true;
}

/**
 * Converts a number to an Outcome type.
 *
 * @param n - The number to be converted
 * @returns The number cast as an Outcome
 */
export function toOutcome(n: number): Outcome {
  return n as Outcome;
}

/**
 * Maps numeric outcome indices to their string representation for 1X2 betting.
 * - 0 maps to '1' (home win)
 * - 1 maps to 'X' (draw)
 * - 2 maps to '2' (away win)
 */
export const indexToOutcomeString: Record<number, string> = {
  0: '1',
  1: 'X',
  2: '2',
} as const;

/**
 * Maps numeric outcome indices to their string representation for 1X2 betting.
 * - 0 maps to '1' (home win)
 * - 1 maps to 'X' (draw)
 * - 2 maps to '2' (away win)
 */
export const outcomeStringToIndex: Record<string, number> = {
  '1': 0,
  X: 1,
  '2': 2,
} as const;

/**
 * Converts a number to an ReductionDistributionOutcome type.
 *
 * @param n - The number to be converted
 * @returns The number cast as an Outcome
 */
export function toReductionDistributionOutcome(n: number): ReductionDistributionOutcome {
  return n as ReductionDistributionOutcome;
}

function getArrayDepth(arr: unknown): number {
  if (!Array.isArray(arr)) {
    return 0;
  }
  return 1 + Math.max(0, ...arr.map((item) => getArrayDepth(item)));
}

/**
 * Checks wether the given value is of type Bet.
 *
 * @param value Value to check
 * @returns True if condition is met
 */
export function isBet(value: unknown): value is Bet {
  return getArrayDepth(value) === 3;
}

/**
 * Checks wether the given value is of type BetEvent.
 *
 * @param value Value to check
 * @returns True if condition is met
 */
export function isBetEvent(value: unknown): value is BetEvent {
  return getArrayDepth(value) === 2;
}

/**
 * Checks wether the given value is of type OutcomeGroup.
 *
 * @param value Value to check
 * @returns True if condition is met
 */
export function isOutcomeGroup(value: unknown): value is OutcomeGroup {
  return getArrayDepth(value) === 1;
}

/**
 * Checks wether the given value is of type Bet[] (array of Bets, single rows).
 *
 * @param value Value to check
 * @returns True if condition is met
 */
export function isBetArray(value: unknown): value is Bet[] {
  return getArrayDepth(value) === 4;
}

/* Types for SROW that powers best bet row etc. */
export type SROW = OutcomeGroup[]; // [[1,2]]

/**
 * Formats a Bet into a string representation for 1X2.
 *
 * @param bet The Bet to be formatted
 * @returns The string representation of the Bet
 * For example, a Bet with two events, where the first event has an OutcomeGroup of [0,1] and the second event has an OutcomeGroup of [2], will be formatted as "1X,2".
 */
export function formatBetTo1X2String(bet: Bet): string {
  return bet
    .map((event) => {
      const outcomeGroup = event[0] || [];
      return outcomeGroup.map((index) => indexToOutcomeString[index]).join('');
    })
    .join(',');
}
